// The mode bar, the rail and, on phones, the bottom bar. NAV-REDESIGN (founder-approved
// 2026-10-29):
// - Top tabs, the work modes: Chat · Work · Calendar · KPIs (Portfolio first in Founder OS).
// - Rail: Desk (was Home; SHELL-DECISIONS 2026-10-30), Inbox, Live (the agents' live screens, was Screens) on top; Workforce, Settings
//   (Setup), the operator console and Me at the foot. Icon over a short label.
// - Reports is a section of KPIs (`/kpis/reports`): no tab or rail item of its own.
// - Phones (< 768 px): the tabs take their own scrolling row in the top bar; the rail becomes the
//   bottom bar: Desk, Inbox, Live, More (Workforce, Settings), Me.
// Everything reads the registry through `shownModes` (one check: role, add-on, grace) and
// switches with `modeLanding`, so the playhead (company, subject, scope, week) carries.
import {
  BottomBar,
  BottomBarItem,
  BottomBarMenu,
  BrandMark,
  DropdownMenuItem,
  ModeTab,
  ModeTabs,
  Rail,
  RailItem,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { EllipsisIcon, LockIcon, ShieldCheckIcon } from "lucide-react";
import { type ComponentProps, type MouseEvent, type ReactNode, useEffect, useState } from "react";
import type { MeResponse } from "../api/types";
import { modeKeyOf, modeLanding } from "../extensions/mode-switch";
import { type ShownMode, shownModes } from "../extensions/modes";
import type { ModeEntry } from "../extensions/registry";
import { screensOverviewQuery } from "../features/agent-screens/screens-queries";
import { type CompanyAccess, isOperator } from "../features/company";
import { useFocus } from "../focus/focus";
import { ADDON_NAMES } from "../pages/status-pages";

/**
 * A link to Setup › Add-ons that is never "current": a router `Link` marks itself current on that
 * page, which would light up every locked mode while the person is on Add-ons.
 */
function AddonsLink({
  slug,
  hash,
  onClick,
  ...props
}: ComponentProps<"a"> & { slug: string; hash: string }) {
  const router = useRouter();
  const target = { to: "/c/$slug/setup/addons", params: { slug }, hash };
  // The same options a `Link` takes; `buildLocation`'s generic signature does not infer them.
  const build = router.buildLocation as unknown as (o: typeof target) => { href: string };
  const location = build(target);
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    e.preventDefault();
    void router.navigate({ href: location.href });
  };
  return <a {...props} href={location.href} onClick={go} />;
}

/** The mode the current path is in (`/c/{slug}/{mode}/…`). */
export function useCurrentModeKey(): string | null {
  return useRouterState({ select: (s) => modeKeyOf(s.location.pathname) });
}

/** A router link that switches to `mode`, landing where the playhead says (`modeLanding`). */
function ModeLink({ mode, ...props }: { mode: ModeEntry } & ComponentProps<"a">) {
  const { focus } = useFocus();
  const landing = modeLanding(mode, focus);
  return (
    <Link
      {...(props as object)}
      to={landing.to as never}
      params={landing.params as never}
      search={landing.search as never}
      // Current-ness is the mode's, not the link's (a DE page is not the mode's index).
      activeOptions={{ exact: true, includeSearch: false }}
    />
  );
}

function lockedLabel(mode: ModeEntry) {
  const addon = ADDON_NAMES[mode.feature];
  return { label: `${mode.label}, ${addon} add-on off`, hint: `${addon} add-on is off · Enable` };
}

const railName = (mode: ModeEntry) => mode.railLabel ?? mode.label;

/** The rail's own check of Live waits this long after a page opens, then repeats this often. */
export const LIVE_DOT_DELAY_MS = 15_000;
export const LIVE_DOT_POLL_MS = 60_000;

/**
 * Live's dot: an agent's screen is live (a DE's or an agent's lease running; a person's own
 * screen or a queued one does not count). It reads the overview Live polls itself (cache first,
 * no call while a page loads: every screen keeps its call budget, spec 13 acceptance 3), then
 * checks once a minute from 15 s after the company opened, while Live is open to the viewer (the
 * `agents` add-on on, a member's mode).
 */
function useLiveActive(enabled: boolean): boolean {
  return useLiveState(enabled).active;
}

/**
 * Live's dot and SCREEN-HANDOFF's "Needs you" count (sessions waiting for this person, the
 * overview's `needs_you`), from one read.
 */
function useLiveState(enabled: boolean): { active: boolean; needsYou: number } {
  const { api } = useRouter().options.context;
  const { focus } = useFocus();
  const co = focus.company.id;
  // Armed per company: switching company waits again before its first check.
  const [armedFor, setArmedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const t = window.setTimeout(() => setArmedFor(co), LIVE_DOT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [enabled, co]);
  const q = useQuery({
    ...screensOverviewQuery(api, co),
    enabled: enabled && armedFor === co,
    refetchInterval: LIVE_DOT_POLL_MS,
    staleTime: LIVE_DOT_POLL_MS / 2,
    retry: false,
  });
  if (!enabled || !q.data) return { active: false, needsYou: 0 };
  return {
    active: q.data.live.some((l) => l.holder.kind !== "person"),
    needsYou: q.data.needs_you.filter((l) => !l.controller).length,
  };
}

/** The work modes, centred in the top bar (a scrolling row of their own on phones). */
export function ShellModeTabs({ me, company }: { me: MeResponse; company: CompanyAccess }) {
  const current = useCurrentModeKey();
  const tabs = shownModes(me, company).filter((i) => i.mode.placement === "tab");
  if (tabs.length === 0) return null;
  return (
    <ModeTabs>
      {tabs.map(({ mode, state }) => {
        if (state === "locked") {
          const { label, hint } = lockedLabel(mode);
          return (
            <ModeTab
              key={mode.key}
              icon={mode.icon}
              label={mode.label}
              state="locked"
              data-locked-mode={mode.key}
              aria-label={label}
              hint={hint}
              render={<AddonsLink slug={company.slug} hash={mode.feature} />}
            />
          );
        }
        return (
          <ModeTab
            key={mode.key}
            icon={mode.icon}
            label={mode.label}
            active={current === mode.key}
            shortcut={mode.digit}
            state={state === "read-only" ? "read-only" : undefined}
            hint={state === "read-only" ? `${mode.label} · read-only (grace period)` : undefined}
            data-mode={mode.key}
            render={<ModeLink mode={mode} />}
          />
        );
      })}
    </ModeTabs>
  );
}

function OperatorItem() {
  return (
    <RailItem
      icon={ShieldCheckIcon}
      label="Operator"
      chord={["g", "p"]}
      hint="Operator console"
      data-mode="operator"
      render={<Link to="/operator" />}
    />
  );
}

/** One rail item for a mode (locked ones, owners' and admins', open Setup › Add-ons). */
function ModeRailItem({
  item: { mode, state },
  company,
  current,
  dot,
  needsYou = 0,
}: {
  item: ShownMode;
  company: CompanyAccess;
  current: string | null;
  dot?: boolean;
  /** SCREEN-HANDOFF: sessions waiting for this person (Live's "Needs you" count). */
  needsYou?: number;
}) {
  if (state === "locked") {
    const { label, hint } = lockedLabel(mode);
    return (
      <RailItem
        icon={mode.icon}
        label={railName(mode)}
        state="locked"
        hint={hint}
        aria-label={label}
        data-locked-mode={mode.key}
        render={<AddonsLink slug={company.slug} hash={mode.feature} />}
      />
    );
  }
  return (
    <RailItem
      icon={mode.icon}
      label={railName(mode)}
      chord={["g", mode.shortcut]}
      hint={
        needsYou > 0
          ? `${railName(mode)} · ${needsYou} need${needsYou === 1 ? "s" : ""} you`
          : dot
            ? `${railName(mode)} · an agent screen is live`
            : (mode.railHint ?? railName(mode))
      }
      aria-label={
        needsYou > 0
          ? `${railName(mode)}, ${needsYou} session${needsYou === 1 ? "" : "s"} need you`
          : dot
            ? `${railName(mode)}, an agent screen is live`
            : undefined
      }
      active={current === mode.key}
      state={state === "read-only" ? "read-only" : undefined}
      dot={dot && needsYou === 0}
      count={needsYou > 0 ? needsYou : undefined}
      data-mode={mode.key}
      data-live={dot ? "true" : undefined}
      data-needs-you={needsYou > 0 ? String(needsYou) : undefined}
      render={<ModeLink mode={mode} />}
    />
  );
}

/**
 * The rail: Desk, Inbox and Live on top; Workforce, Settings, the operator console and Me at the
 * foot. `company` null is the operator console's rail (Operator and Me only).
 */
export function ShellRail({
  me,
  company,
  account,
  inbox,
}: {
  me: MeResponse;
  company: CompanyAccess | null;
  /** Me: the account menu, at the rail's foot (wide screens; phones have it in the bottom bar). */
  account?: ReactNode;
  /** The Inbox item (inbox-peek.tsx), under Desk. */
  inbox?: ReactNode;
}) {
  const brand = (
    <BrandMark
      name="CompanyOS"
      render={
        company ? (
          <Link to="/c/$slug/desk" params={{ slug: company.slug }} />
        ) : (
          <Link to="/operator" />
        )
      }
    />
  );
  const foot = (
    <>
      {isOperator(me) ? <OperatorItem /> : null}
      {account ? <li className="mt-1 flex justify-center">{account}</li> : null}
    </>
  );
  if (!company)
    return (
      <Rail aria-label="Global" brand={brand} bottom={foot}>
        {null}
      </Rail>
    );
  return <CompanyRail me={me} company={company} brand={brand} foot={foot} inbox={inbox} />;
}

function CompanyRail({
  me,
  company,
  brand,
  foot,
  inbox,
}: {
  me: MeResponse;
  company: CompanyAccess;
  brand: ReactNode;
  foot: ReactNode;
  inbox?: ReactNode;
}) {
  const current = useCurrentModeKey();
  const items = shownModes(me, company);
  const top = items.filter((i) => i.mode.placement === "global");
  const bottom = items.filter((i) => i.mode.placement === "foot");
  const desk = top.filter((i) => i.mode.key === "desk");
  const rest = top.filter((i) => i.mode.key !== "desk");
  const live = rest.find((i) => i.mode.key === "live");
  const liveState = useLiveState(!!live && live.state !== "locked");
  const item = (i: ShownMode) => (
    <ModeRailItem
      key={i.mode.key}
      item={i}
      company={company}
      current={current}
      dot={i.mode.key === "live" && liveState.active}
      needsYou={i.mode.key === "live" ? liveState.needsYou : 0}
    />
  );
  return (
    <Rail
      aria-label="Global"
      brand={brand}
      bottom={
        <>
          {bottom.map(item)}
          {foot}
        </>
      }
    >
      {desk.map(item)}
      {inbox}
      {rest.map(item)}
    </Rail>
  );
}

/**
 * Phones (< 768 px, the rail hidden): the rail as a bottom bar — Desk, Inbox, Live, then "More"
 * (Workforce, Settings, locked items for owners and admins) and Me. The work modes stay in the
 * top bar's scrolling tab row.
 */
export function MobileModeBar({
  me,
  company,
  inbox,
  account,
}: {
  me: MeResponse;
  company: CompanyAccess;
  /** The Inbox's bottom-bar item (inbox-peek.tsx). */
  inbox?: ReactNode;
  /** Me: the account menu's bottom-bar item (me-menu.tsx). */
  account?: ReactNode;
}) {
  const current = useCurrentModeKey();
  const router = useRouter();
  const { focus } = useFocus();
  const items = shownModes(me, company);
  const top = items.filter((i) => i.mode.placement === "global" && i.state !== "locked");
  const more = items
    .filter(
      (i) => i.mode.placement === "foot" || (i.mode.placement === "global" && i.state === "locked"),
    )
    .sort((a, b) => a.mode.mobileOrder - b.mode.mobileOrder);
  const live = top.find((i) => i.mode.key === "live");
  const liveActive = useLiveActive(!!live);
  const desk = top.filter((i) => i.mode.key === "desk");
  const rest = top.filter((i) => i.mode.key !== "desk");
  const barItem = ({ mode }: ShownMode) => (
    <BottomBarItem
      key={mode.key}
      icon={mode.icon}
      label={railName(mode)}
      active={current === mode.key}
      dot={mode.key === "live" && liveActive}
      aria-label={
        mode.key === "live" && liveActive ? `${railName(mode)}, an agent screen is live` : undefined
      }
      data-mode={mode.key}
      render={<ModeLink mode={mode} />}
    />
  );
  return (
    <BottomBar>
      {desk.map(barItem)}
      {inbox}
      {rest.map(barItem)}
      {more.length ? (
        <BottomBarMenu
          icon={EllipsisIcon}
          label="More"
          active={more.some((i) => i.state !== "locked" && i.mode.key === current)}
        >
          {more.map(({ mode, state }) => {
            const Icon = mode.icon;
            if (state === "locked") {
              const { label } = lockedLabel(mode);
              return (
                <DropdownMenuItem
                  key={mode.key}
                  data-locked-mode={mode.key}
                  aria-label={label}
                  onClick={() =>
                    void router.navigate({
                      to: "/c/$slug/setup/addons",
                      params: { slug: company.slug },
                      hash: mode.feature,
                    })
                  }
                >
                  <Icon aria-hidden="true" />
                  {railName(mode)}
                  <LockIcon aria-hidden="true" className="ml-auto" />
                </DropdownMenuItem>
              );
            }
            const landing = modeLanding(mode, focus);
            return (
              <DropdownMenuItem
                key={mode.key}
                data-mode={mode.key}
                onClick={() => void router.navigate(landing as never)}
              >
                <Icon aria-hidden="true" />
                {railName(mode)}
                {mode.digit ? (
                  <span className="ml-auto text-xs text-muted-foreground">{mode.digit}</span>
                ) : null}
              </DropdownMenuItem>
            );
          })}
        </BottomBarMenu>
      ) : null}
      {account}
    </BottomBar>
  );
}
